import {
  Backdrop,
  Box,
  CardHeader,
  Container,
  LinearProgress,
  Portal,
  Skeleton,
  Stack,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import { LogoText } from '../../assets/logo-text';
import { useMuiSwitchableTheme } from '../../theme';

const delayedLoadingWrapper =
  (Component: React.FC) =>
  // eslint-disable-next-line react/display-name
  ({ delay = 0 }: { delay?: number }) => {
    const [show, setShow] = useState(delay === 0);

    useEffect(() => {
      const timer = setTimeout(() => setShow(true), delay);
      return () => clearTimeout(timer);
    }, [delay]);

    if (!show) return null;

    return <Component />;
  };

export const Loading = delayedLoadingWrapper(() => (
  <Container sx={{ width: 1 }}>
    <CardHeader
      title={<Skeleton height={40} sx={{ m: -0.5 }} />}
      subheader={<Skeleton height={20} sx={{ m: -0.5 }} />}
    />
    <Skeleton variant="rectangular" height={4} sx={{ mx: 2, mt: 1 }} />
    <BlockLoading />
  </Container>
));

export const BlockLoading = delayedLoadingWrapper(() => (
  <Stack spacing={2} direction="column" component={Container} sx={{ py: 2 }}>
    <Skeleton variant="rectangular" height={200} />
    <Skeleton variant="rectangular" height={75} />
    <Skeleton variant="rectangular" height={75} />
  </Stack>
));

export const ColumnLoading = delayedLoadingWrapper(() => (
  <Stack
    spacing={2}
    direction={{ xs: 'column', md: 'row' }}
    sx={{ p: 2 }}
    component={Container}
  >
    <Skeleton
      variant="rectangular"
      height="80vh"
      sx={{ width: { xs: 1, md: 0.3 } }}
    />
    <Skeleton
      variant="rectangular"
      height="80vh"
      width="70%"
      sx={{ display: { xs: 'none', md: 'flex' } }}
    />
  </Stack>
));

export const FooterLoading = delayedLoadingWrapper(() => {
  const { marginLeft } = useMuiSwitchableTheme();
  return (
    <Portal>
      <LinearProgress
        sx={{
          position: 'fixed',
          bottom: 10,
          left: marginLeft + 10,
          right: 10,
          zIndex: 1000,
          borderRadius: 1,
          height: 6,
        }}
      />
    </Portal>
  );
});

export const FullscreenLoading = delayedLoadingWrapper(() => {
  const theme = useTheme();

  return (
    <Backdrop
      open
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, display: 'flex' }}
    >
      <Box
        sx={{
          animation: `pulse 5000ms ${theme.transitions.easing.easeInOut}`,
          animationIterationCount: 'infinite',
          '@keyframes pulse': {
            '0%': { opacity: 0.5, transform: 'scale(1)' },
            '50%': { opacity: 1, transform: 'scale(1.2)' },
            '100%': { opacity: 0.5, transform: 'scale(1)' },
          },
        }}
      >
        <LogoText color="success.main" />
      </Box>
    </Backdrop>
  );
});
